
import React, { Fragment, useEffect, useState } from "react";

import {
  Column,
  Table as ReactTable,
  ColumnFiltersState,
  SortingState,
  FilterFn,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender
} from '@tanstack/react-table';

import { rankItem } from '@tanstack/match-sorter-utils';
import {
  Minus,
  Plus,
  Search,
  FileSpreadsheet,
  Loader2,
  Inbox,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
} from "lucide-react";
import Pagination from "./Components/Pagination";
import { exportToExcel } from "./Components/Excel";
import TableDropDownSmall from "pages/Components/TableDropDownSmall";

// Column Filter
const Filter = ({
  column
}: {
  column: Column<any, unknown>;
  table: ReactTable<any>;
}) => {
  const columnFilterValue = column.getFilterValue();

  return (
    <>
      <DebouncedInput
        type="text"
        value={(columnFilterValue ?? '') as string}
        onChange={value => column.setFilterValue(value)}
        placeholder="Search..."
        className="w-36 border shadow rounded"
        list={column.id + 'list'}
      />
      <div className="h-1" />
    </>
  );
};

// Global Filter
const DebouncedInput = ({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number;
  onChange: (value: string | number) => void;
  debounce?: number;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);


  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [debounce, onChange, value]);

  return (
    <input {...props} value={value} onChange={e => setValue(e.target.value)} />
  );
};
interface TableContainerProps {
  columns?: any;
  data?: any;
  tableclassName?: any;
  divclassName?: any;
  thclassName?: any;
  trclassName?: any;
  tableClass?: any;
  tdclassName?: any;
  theadclassName?: any;
  tbodyclassName?: any;
  isTfoot?: boolean;
  isSelect?: boolean;
  isBordered?: boolean;
  customPageSize?: number;
  isGlobalFilter?: boolean;
  /** نمایش جست‌وجوی داخلی جدول؛ برای صفحاتی که جست‌وجوی سروری جداگانه دارند خاموش شود. */
  showGlobalSearch?: boolean;
  isPagination: boolean;
  PaginationClassName?: string;
  SearchPlaceholder?: string;
  customnumberfortable?: number;
  /** تعداد ستون نمایشی در موبایل (کمتر از ۶۴۰ پیکسل). پیش‌فرض ۳. */
  customnumberformobile?: number;
  /**
   * برگرداننده‌ی «همه‌ی» رکوردها برای خروجی اکسل. وقتی صفحه‌بندی سمت سرور است،
   * جدول فقط رکوردهای همان صفحه را دارد؛ این تابع بقیه را هم می‌آورد.
   * اگر داده نشود، خروجی اکسل از همان رکوردهای روی صفحه ساخته می‌شود.
   */
  exportAllRows?: () => Promise<any[]>;
  // وقتی صفحه‌بندی واقعاً سمت سرور انجام می‌شود (یعنی `data` فقط شامل رکوردهای همان صفحه است،
  // نه کل دیتاست)، این prop را بدهید تا به‌جای صفحه‌بندی محلی روی همین چند ردیف، شماره صفحات
  // واقعی از سرور نمایش داده شود و با تغییر صفحه، fetch جدید بزند.
  serverPagination?: {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    // اندازه صفحه‌ی واقعی سمت سرور و تابع تغییرش. اگر داده شود، دراپ‌داون «سطر در صفحه»
    // همین را کنترل می‌کند (نه pageSize محلی react-table که فقط برای نمایش همان یک صفحه‌ی
    // دریافتی از سرور است و نباید با اندازه‌ی واقعی درخواست قاطی شود).
    pageSize?: number;
    onPageSizeChange?: (size: number) => void;
  };
  /** مرتب‌سازی API؛ با فعال بودن این گزینه هیچ مرتب‌سازی محلی انجام نمی‌شود. */
  serverSorting?: {
    sortBy?: string;
    sortOrder: "default" | "asc" | "desc";
    onChange: (sortBy: string | undefined, sortOrder: "default" | "asc" | "desc") => void;
  };
}

const TableContainer = ({
  columns,
  data,
  tableclassName,
  theadclassName,
  divclassName,
  trclassName,
  thclassName,
  tdclassName,
  tbodyclassName,
  isTfoot,
  isSelect,
  isPagination,
  customPageSize,
  isGlobalFilter,
  showGlobalSearch = true,
  PaginationClassName,
  SearchPlaceholder,
  customnumberfortable,
  customnumberformobile,
  exportAllRows,
  serverPagination,
  serverSorting
}: TableContainerProps) => {

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const sorting: SortingState = React.useMemo(() => {
    if (!serverSorting?.sortBy || serverSorting.sortOrder === "default") return [];
    const match = (columns || []).find((column: any) =>
      (column.meta?.serverSortBy || column.accessorKey || column.id) === serverSorting.sortBy
    );
    return match ? [{ id: match.id || match.accessorKey, desc: serverSorting.sortOrder === "desc" }] : [];
  }, [columns, serverSorting?.sortBy, serverSorting?.sortOrder]);

  const handleServerSortingChange = React.useCallback(
    (updater: any) => {
      if (!serverSorting) return;
      const next: SortingState = typeof updater === "function" ? updater(sorting) : updater;
      const first = next[0];
      if (!first) {
        serverSorting.onChange(undefined, "default");
        return;
      }
      const column = (columns || []).find((item: any) => (item.id || item.accessorKey) === first.id);
      const sortBy = column?.meta?.serverSortBy || column?.accessorKey || column?.id;
      if (sortBy) serverSorting.onChange(sortBy, first.desc ? "desc" : "asc");
    },
    [columns, serverSorting, sorting]
  );

  const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta({
      itemRank
    });
    return itemRank.passed;
  };


  // ستون‌های دکمه‌ای (مشخصات، ثبت پرداخت، عملیات، ...) مقدار متنی ندارند و
  // مرتب‌سازی روی آن‌ها بی‌معنا است. همان نشانه‌ی exportable === false که برای
  // خروجی اکسل استفاده می‌شود، اینجا هم ملاک است.
  const sortableColumns = React.useMemo(
    () =>
      (columns || []).map((column: any) =>
        column?.enableSorting === undefined &&
        (column?.exportable === false || (!column?.accessorKey && !column?.meta?.serverSortBy))
          ? { ...column, enableSorting: false }
          : column
      ),
    [columns]
  );

  const table = useReactTable({
    columns: sortableColumns,
    data,
    // چرخه‌ی سه‌حالته‌ی مستند: ترتیب ورود ← صعودی ← نزولی ← ترتیب ورود.
    // sortDescFirst=false یعنی اولین کلیک همیشه صعودی است (حتی برای ستون عددی)
    // و enableSortingRemoval اجازه می‌دهد کلیک سوم به حالت پیش‌فرض برگردد.
    sortDescFirst: false,
    enableSortingRemoval: true,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      columnFilters,
      globalFilter,
      ...(serverSorting ? { sorting } : {}),
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    ...(serverSorting
      ? { manualSorting: true, enableSorting: true, onSortingChange: handleServerSortingChange }
      : { enableSorting: false, getSortedRowModel: getSortedRowModel() })
  });

  const {
    getHeaderGroups,
    getFooterGroups,
    getAllLeafColumns,
    getRowModel,
    getPageOptions,
    setPageIndex,
    setPageSize,
    getState,
  } = table;

  useEffect(() => {
    if (serverPagination) {
      // دیتای دریافتی همان یک صفحه‌ی سرور است؛ نباید دوباره به‌صورت محلی تکه‌تکه شود.
      setPageSize(data?.length || 1);
    } else if (Number(customPageSize)) {
      setPageSize(Number(customPageSize));
    }
  }, [customPageSize, setPageSize, serverPagination, data]);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null); // تغییر نوع به string | null

  // عرض پنجره در state نگه داشته می‌شود، نه اینکه هنگام رندر مستقیم از window خوانده شود.
  // قبلاً فقط عبور از مرز ۷۶۸ باعث رندر دوباره می‌شد، پس با تغییر عرض بین مرزهای دیگر
  // تعداد ستون‌های نمایشی به‌روز نمی‌شد.
  const [windowWidth, setWindowWidth] = useState<number>(
    typeof window !== "undefined" ? window.innerWidth : 1280
  );

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ستون‌های بیشتر از این تعداد پنهان می‌شوند و در دراپ‌داون هر ردیف دیده می‌شوند، تا
  // جدول‌های عریض (مثل سفارش‌ها با ۱۵ ستون) صفحه را به اسکرول افقی نیندازند.
  const getVisibleColumns = () => {
    const numberForTable = customnumberfortable ?? 8;
    if (windowWidth < 640) return customnumberformobile ?? 3; // موبایل
    if (windowWidth < 1024) return 5; // تبلت
    if (windowWidth < 1280) return Math.min(numberForTable, 6); // لپ‌تاپ کوچک
    if (windowWidth < 1536) return Math.min(numberForTable, 8); // لپ‌تاپ
    return numberForTable; // نمایشگر عریض
  };

  const visibleColumns = getVisibleColumns()

  // خروجی اکسل باید دقیقاً همان ستون‌هایی باشد که روی صفحه دیده می‌شوند، با همان
  // عنوان فارسی و همان مقدار نمایشی — نه رکورد خام سرور با کلیدهای انگلیسی.
  // ستون‌هایی که سلولشان محاسباتی است می‌توانند exportValue بدهند.
  const [exporting, setExporting] = useState<boolean>(false);

  const buildExportRows = (records: any[]) => {
    const leafColumns = getAllLeafColumns().filter((column: any) => {
      const def = column.columnDef || {};
      // ستون‌های عملیاتی (دکمه‌ها) در اکسل معنا ندارند.
      return def.header && def.exportable !== false;
    });

    return records.map((record: any) => {
      const out: Record<string, any> = {};
      leafColumns.forEach((column: any) => {
        const def = column.columnDef || {};
        const title = typeof def.header === "string" ? def.header : column.id;
        if (typeof def.exportValue === "function") {
          out[title] = def.exportValue(record);
        } else if (def.accessorKey !== undefined) {
          const value = record[def.accessorKey];
          out[title] = value === null || value === undefined ? "" : value;
        } else {
          out[title] = "";
        }
      });
      return out;
    });
  };

  const handleExportExcel = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      // وقتی صفحه‌بندی سمت سرور است، رکوردهای روی صفحه فقط یک صفحه‌اند؛
      // exportAllRows کل دیتاست را می‌آورد.
      const records = exportAllRows
        ? await exportAllRows()
        : getRowModel().rows.map((row: any) => row.original);

      const fileName = (document.title || "خروجی-جدول").split("/")[0].trim();
      exportToExcel(buildExportRows(records), fileName);
    } catch (error) {
      console.error("Error exporting excel:", error);
    } finally {
      setExporting(false);
    }
  };

  const renderCellValue = (cell: any) => {
    const hasAccessor =
      cell.column.columnDef.accessorKey !== undefined ||
      cell.column.columnDef.accessorFn !== undefined;
    if (hasAccessor) {
      const value = cell.getValue();
      if (value === null || value === undefined || value === "") {
        return <span className="text-slate-400 dark:text-zink-400">-</span>;
      }
    }
    return flexRender<any>(cell.column.columnDef.cell, cell.getContext());
  };

  return (
    <Fragment>
      <div className="flex flex-col gap-3 mb-4 md:flex-row md:items-center md:justify-between">
        {showGlobalSearch && (
          <div className="relative w-full md:max-w-xs group/search">
            <Search className="absolute inset-y-0 right-3.5 my-auto h-4 w-4 text-slate-400 dark:text-zink-300 group-focus-within/search:text-custom-500 transition-colors" />
            <DebouncedInput
              value={globalFilter ?? ''}
              onChange={(value) => {
                if (typeof value === 'string') {
                  setGlobalFilter(value.trim());
                } else {
                  setGlobalFilter(String(value));
                }
              }}
              placeholder="جست‌وجو..."
              className="w-full rounded-xl border border-slate-200 dark:border-zink-500 bg-slate-50/60 dark:bg-zink-700 py-2.5 pr-10 pl-3 text-sm text-slate-700 dark:text-zink-100 placeholder:text-slate-400 dark:placeholder:text-zink-300 outline-none transition-all duration-200 focus:border-custom-400 focus:bg-white dark:focus:bg-zink-700 focus:ring-4 focus:ring-custom-500/10"
            />
          </div>
        )}

        <div className="flex items-center gap-3 shrink-0">
          <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-zink-200">
            سطر در صفحه
            <TableDropDownSmall
              value={serverPagination?.pageSize ?? getState().pagination.pageSize}
              onSelect={(size) => {
                if (serverPagination?.onPageSizeChange) {
                  serverPagination.onPageSizeChange(size);
                } else {
                  setPageSize(size);
                }
              }}
            />
          </div>

          <button
            type="button"
            onClick={handleExportExcel}
            disabled={exporting}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 dark:border-zink-500 bg-white dark:bg-zink-700 px-3.5 py-2.5 text-sm text-slate-600 dark:text-zink-100 transition-all duration-200 hover:border-custom-400 hover:text-custom-600 dark:hover:text-custom-400 hover:shadow-sm disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 animate-spin text-custom-500" />
            ) : (
              <FileSpreadsheet className="w-4 h-4" />
            )}
            {exporting ? "در حال آماده‌سازی..." : "خروجی اکسل"}
          </button>
        </div>
      </div>

      <div className={divclassName}>
        <table className={tableclassName}>

          <thead className={theadclassName}>
            {getHeaderGroups().map((headerGroup: any) => (
              <tr key={headerGroup.id} className={typeof trclassName === "function" ? undefined : trclassName}>
                {headerGroup.headers.map((header: any, index: number) => {
                  const canSort = header.column.getCanSort();
                  const sorted = header.column.getIsSorted();
                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      className={`${thclassName} ${canSort ? "cursor-pointer select-none" : "cursor-default"
                        } ${index >= visibleColumns ? "hidden" : ""}`} // ستون‌های مخفی
                      onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                      title={
                        canSort
                          ? sorted === "asc"
                            ? "مرتب‌شده صعودی — برای نزولی کلیک کنید"
                            : sorted === "desc"
                              ? "مرتب‌شده نزولی — برای بازگشت به ترتیب ورود کلیک کنید"
                              : "برای مرتب‌سازی صعودی کلیک کنید"
                          : undefined
                      }
                    >
                      {header.isPlaceholder ? null : (
                        <span className="inline-flex items-center gap-1">
                          {flexRender<any>(header.column.columnDef.header, header.getContext())}
                          {/* نشانه‌ی مرتب‌سازی: کم‌رنگ یعنی «قابل مرتب‌سازی ولی
                              مرتب‌نشده»، پررنگ یعنی جهت فعلی. */}
                          {canSort &&
                            (sorted === "asc" ? (
                              <ChevronUp className="size-3.5 shrink-0" />
                            ) : sorted === "desc" ? (
                              <ChevronDown className="size-3.5 shrink-0" />
                            ) : (
                              <ChevronsUpDown className="size-3.5 shrink-0 opacity-40" />
                            ))}
                        </span>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          <tbody className={tbodyclassName}>
            {getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={Math.max(columns?.length || 1, 1)} className="py-16">
                  <div className="flex flex-col items-center justify-center gap-3 text-center">
                    <div className="flex items-center justify-center rounded-full bg-slate-100 dark:bg-zink-700 size-16">
                      <Inbox className="text-slate-400 dark:text-zink-300 size-8" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-600 dark:text-zink-100">داده‌ای یافت نشد</p>
                      <p className="mt-1 text-xs text-slate-400 dark:text-zink-300">
                        هیچ رکوردی برای نمایش وجود ندارد
                      </p>
                    </div>
                  </div>
                </td>
              </tr>
            ) : (
              getRowModel().rows.map((row: any) => (
              <React.Fragment key={row.id}>
                {/* trclassName می‌تواند رشته باشد یا تابعی از ردیف، تا صفحاتی که نیاز به
                    رنگ‌بندی هر ردیف دارند (مثل گروه‌بندی بر اساس شماره فاکتور) بتوانند
                    کلاس متفاوتی به هر ردیف بدهند. */}
                <tr
                  className={
                    typeof trclassName === "function"
                      ? trclassName(row.original, row)
                      : trclassName
                  }
                >
                  {row.getVisibleCells().map((cell: any, index: number) => (
                    <td
                      key={cell.id}
                      className={`${tdclassName} ${index >= visibleColumns ? 'hidden' : ''}`} // ستون‌های مخفی
                    >
                      {index === 0 ? (
                        <div className="flex items-center">
                          {openDropdown === row.id ? (
                            <Minus
                              className="dark:text-white w-4 h-4 flex text-xs border-2 ml-2 dark:border-white text-custom-600 border-custom-600 rounded-full cursor-pointer"
                              onClick={() => setOpenDropdown(null)}
                            />
                          ) : (
                            <Plus
                              className="dark:text-white w-4 h-4 flex text-xs border-2 ml-2 dark:border-white text-custom-600 border-custom-600 rounded-full cursor-pointer"
                              onClick={() => setOpenDropdown(row.id)}
                            />
                          )}
                          {renderCellValue(cell)}
                        </div>
                      ) : (
                        renderCellValue(cell)
                      )}
                    </td>
                  ))}
                </tr>
                {openDropdown === row.id && (
                  <tr>
                    <td colSpan={row.getVisibleCells().length} className="pb-1">
                      <div className="bg-gray-100 dark:bg-gray-800 rounded shadow-md">
                        <div className="block ">
                          {row.getVisibleCells().map((cell: any, index: number) => (
                            <div
                              key={cell.id}
                              className="flex dark:border-blue-900  hover:mr-1 hover:cursor-pointer border-blue-100 border-b flex-col w-full">
                              {index >= visibleColumns && ( // ستون‌های مخفی در دراپ‌دان
                                <div className="flex w-full gap-4">
                                  {/* Header Section */}
                                  <div className="font-bold lg:p-1 pb-1   shrink-0 w-1/3 md:w-1/4">
                                    {cell.column.columnDef.header ? (
                                      typeof cell.column.columnDef.header === 'function'
                                        ? flexRender<any>(cell.column.columnDef.header, cell.getContext())
                                        : cell.column.columnDef.header
                                    ) : null}

                                  </div>
                                  {/* Value Section */}
                                  <div className="flex-1  sm:pt-0   mr-[4.6rem] lg:mr-0 ">
                                    {renderCellValue(cell)}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}

              </React.Fragment>
              ))
            )}
          </tbody>

          {isTfoot && <tfoot>
            {
              getFooterGroups()?.map((footer: any, tfKey: number) => (
                <tr key={tfKey}>
                  {
                    footer.headers?.map((tf: any, key: number) => (
                      <th key={key} className="p-3  text-left group-[.bordered]:border group-[.bordered]:border-slate-200 group-[.bordered]:dark:border-zink-500">
                        {flexRender(
                          tf.column.columnDef.header,
                          tf.getContext()
                        )}
                      </th>
                    ))
                  }
                </tr>
              ))
            }
          </tfoot>}
        </table>

      </div>

      {isPagination && (
        serverPagination ? (
          <Pagination
            currentPage={serverPagination.currentPage}
            totalPages={Math.max(serverPagination.totalPages, 1)}
            onPageChange={serverPagination.onPageChange}
          />
        ) : (
          <Pagination
            currentPage={getState().pagination.pageIndex + 1}
            totalPages={Math.max(getPageOptions().length, 1)}
            onPageChange={(page) => setPageIndex(page - 1)}
          />
        )
      )}
    </Fragment>
  );
};

export default TableContainer;
